import {SelectPlayerFormat} from '@colonist/game-base-types'
import {SectionBgAdaptiveView} from '../../ui_canvas_engine/SectionBgViewAdaptive'
import {ACTIVE_OPACITY} from '../../ui_utils/Utils'
import {UIGameManager} from '../UIGameManager'
import {UIGamePopupConfirmationButtons} from './UIGamePopupConfirmationButtons'
import {UIGamePlayerAvatarContainerView} from './UIGamePlayerAvatarContainerView'
import {PIXI} from '@colonist/pixi'
import {SpriteView} from '../../ui_canvas_engine/SpriteView'
import {Sound} from '../../Sound'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {Sprites} from '../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {ActionState, CardEnum, PlayerColor} from '@colonist/game-states'

export class UIGamePopupSelectPlayers extends SectionBgAdaptiveView {
    private readonly uiGameManager: UIGameManager
    private readonly selectPlayerFormat: SelectPlayerFormat
    private readonly selectPlayerCallbacks: {selectPlayerAction: (player: PlayerColor) => void}[]
    private readonly popupImageAndDescription: PIXI.Container
    private playerSelectorContainer: UIGamePlayerAvatarContainerView
    private confirmationButtons: UIGamePopupConfirmationButtons
    private readonly developmentCardUsed: CardEnum | undefined

    private onCancelButton: () => void

    private cancelStateButton: SpriteView | undefined
    private selectedPlayer: PlayerColor | undefined

    // TODO: Remove me while refactoring UIUtilsEjsVariables.shouldDisplayNewMobileGameUI
    private readonly isUserOnMobileApp: boolean

    constructor(uiGameManager: UIGameManager, popupImageAndDescription: PIXI.Container, selectPlayerFormat: SelectPlayerFormat, developmentCardUsed?: CardEnum) {
        super()
        this.uiGameManager = uiGameManager
        this.selectedPlayer = undefined
        this.selectPlayerFormat = selectPlayerFormat
        this.selectPlayerCallbacks = []
        this.developmentCardUsed = developmentCardUsed
        const canvas = this.uiGameManager.canvasController
        canvas.app.stage.addChild(this)

        this.popupImageAndDescription = popupImageAndDescription
        this.addChild(this.popupImageAndDescription)

        this.isUserOnMobileApp = UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI
        this.createPlayerSelectorContainer()
        this.createConfirmationButtons()
        this.addEventToCloseUIOnTapOffView()
        this.drawTextureBgAroundContent()
        this.setContainerY()
    }

    private createPlayerSelectorContainer() {
        const playerAvatarClickAction = (player: PlayerColor) => {
            this.playerClickPlayerAvatar(player)
        }
        const canvas = this.uiGameManager.canvasController
        const playerSelectorY = this.popupImageAndDescription.y + this.popupImageAndDescription.height
        const showPlayerName = true
        const topPadding = canvas.itemSize * 0.075
        const h = canvas.itemSize * (this.isUserOnMobileApp ? 1.4 : 0.95)
        const playerAvatarContainerViewOptions = {
            playerAvatarClickAction: playerAvatarClickAction,
            showPlayerNames: showPlayerName,
            topPadding: topPadding,
            shouldSpaceEvenly: false,
        }
        this.playerSelectorContainer = new UIGamePlayerAvatarContainerView(this.uiGameManager, 0, playerSelectorY, canvas.popupWidth, h, this.selectPlayerFormat.playersToSelect, this, playerAvatarContainerViewOptions)
    }

    private createConfirmationButtons() {
        const clickedCheckAction = () => this.clickedCheckAction()
        this.onCancelButton = () => this.clickedCancelAction()

        const canvas = this.uiGameManager.canvasController
        const buttonH = canvas.itemSize * (this.isUserOnMobileApp ? 1 : 0.6)

        const bottomOfPopupImageAndDescription = this.popupImageAndDescription.y + this.popupImageAndDescription.height
        const paddingWeb = canvas.itemSize * 0.075
        const topMarginToCenterButton = (this.playerSelectorContainer.height / 2) - (buttonH / 2)
        const buttonY = bottomOfPopupImageAndDescription + topMarginToCenterButton
        const buttonW = canvas.popupWidth - (this.isUserOnMobileApp ? 0 : paddingWeb)

        const showCancelButton = this.selectPlayerFormat.cancelButtonActive && !this.isUserOnMobileApp
        this.confirmationButtons = new UIGamePopupConfirmationButtons(0, buttonY, buttonW, buttonH, clickedCheckAction, this.onCancelButton, this, showCancelButton)
    }

    private addEventToCloseUIOnTapOffView() {
        if(!this.isUserOnMobileApp) return
        if(!this.selectPlayerFormat.cancelButtonActive) return
        this.uiGameManager.mapController.mapView.on('tap', this.onCancelButton)
    }

    private setContainerY() {
        const canvas = this.uiGameManager.canvasController
        this.y = canvas.appH - canvas.bottomViewHeight - this.absoluteHeight
    }

    private playerClickPlayerAvatar(player: PlayerColor) {
        this.selectedPlayer = player
        this.playerSelectorContainer.updatePlayerAlpha(player, ACTIVE_OPACITY)
        this.updateCheckmark()
        for(const selectPlayerCallbak of this.selectPlayerCallbacks) {
            selectPlayerCallbak.selectPlayerAction(player)
        }
    }

    addSelectPlayerCallback(selectPlayerAction: (player: PlayerColor) => void) {
        this.selectPlayerCallbacks.push({selectPlayerAction: selectPlayerAction})
    }

    private clickedCheckAction() {
        if(this.selectedPlayer == undefined) return
        this.triggerPlayedDevelopmentCard()
        this.uiGameManager.socketGameSend.selectPlayer(this.selectedPlayer)
        this.closeUI()
    }

    private triggerPlayedDevelopmentCard() {
        if(this.developmentCardUsed == undefined) return
        if(this.uiGameManager.gameController.currentState.actionState != ActionState.None) return
        this.uiGameManager.socketGameSend.clickedDevelopmentCard(this.developmentCardUsed)
    }

    private clickedCancelAction() {
        if(!this.selectPlayerFormat.cancelButtonActive) return
        this.uiGameManager.socketGameSend.cancelAction()
        this.closeUI()
    }

    private updateCheckmark() {
        this.confirmationButtons.checkButton.inactivateOpacity()
        if(this.selectedPlayer == undefined) return

        this.confirmationButtons.checkButton.activateOpacity()
    }

    addCancelStateButton(action: () => void) {
        const canvas = this.uiGameManager.canvasController
        const cancelButtonPadding = canvas.itemSize * 0.1
        const cancelButtonSize = canvas.itemSize * 0.30
        const cancelButtonX = this.width - cancelButtonSize - cancelButtonPadding + canvas.margin
        const cancelButtonY = cancelButtonPadding - canvas.margin
        this.cancelStateButton = new SpriteView(cancelButtonX, cancelButtonY, cancelButtonSize, cancelButtonSize, this.playerSelectorContainer, UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_x))

        if(this.cancelStateButton.cursor != 'cursor') this.cancelStateButton.cursor = 'pointer'
        if(!this.cancelStateButton.interactive) this.cancelStateButton.interactive = true
        this.cancelStateButton.on('click', (event) => {
            action()
            Sound.playSound(Sound.SoundType.Click)
        })
        this.cancelStateButton.on('tap', (event) => {
            action()
            Sound.playSound(Sound.SoundType.Click)
        })
    }

    closeUI() {
        if(this.parent == undefined) return
        this.parent.removeChild(this)
        this.destroy()
    }

    destroy() {
        this.uiGameManager.mapController.mapView.off('tap', this.onCancelButton)
        super.destroy()
    }

    removeConfirmationButtons() {
        if(this.confirmationButtons.parent == undefined) return
        this.confirmationButtons.parent.removeChild(this.confirmationButtons)
    }
}
